import React, { useState } from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// About approach — faithful rewrite of legacy molecules/AboutUsApproach (Strapi
// sections.about-approach). A tabbed section: a vertical list of clickable
// titles on the left (active one shows an accent bar + its description), and the
// active tab's image on the right. Active tab via client useState (SSR tab 0).
export const aboutApproachSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  description: z.string().optional(),
  cards: z
    .array(
      z.object({
        title: z.string().min(1),
        description: z.string().optional(),
        imageUrl: z.string().optional(),
      }),
    )
    .min(1),
});

type AboutApproachProps = z.infer<typeof aboutApproachSchema>;

const Section = styled.section`
  max-width: 112rem;
  margin: 0 auto;
  padding: 4rem 1.6rem;

  @media (min-width: 48em) {
    padding: 14rem 1.6rem;
  }
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  font-size: 1.6rem;
  letter-spacing: 5px;
  text-transform: uppercase;

  @media (min-width: 48em) {
    font-size: 1.8rem;
  }
`;

const HeadRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  margin-top: 1rem;

  @media (min-width: 48em) {
    grid-template-columns: 1fr 1.4fr;
  }
`;

const Heading = styled.h2`
  margin: 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.8rem;
  letter-spacing: -2px;

  @media (min-width: 48em) {
    font-size: 5.6rem;
  }
`;

const HeadDesc = styled.p`
  margin: 1.2rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  line-height: 1.6;

  @media (min-width: 48em) {
    font-size: 1.8rem;
    max-width: 54.8rem;
    margin-left: auto;
  }
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  margin-top: 2rem;

  @media (min-width: 48em) {
    grid-template-columns: 1fr 1.4fr;
    margin-top: 6.4rem;
  }
`;

const List = styled.div``;

const Item = styled.div`
  display: grid;
  grid-template-columns: 6px auto;
  column-gap: 1.6rem;
  margin-top: 3rem;

  @media (min-width: 48em) {
    column-gap: 3rem;
  }
`;

const Rail = styled.div<{ $active: boolean }>`
  width: 0.6rem;
  border-radius: 0.2rem;
  background: ${({ $active }) => ($active ? '#e6ebf5' : 'transparent')};
  height: ${({ $active }) => ($active ? '16.8rem' : '0')};
  transition: all 0.6s ease-in-out;
`;

const RailFill = styled.div`
  width: 0.6rem;
  border-radius: 0.2rem;
  background: #0a3aa8;
  height: 4.7rem;
`;

const ItemBody = styled.div<{ $last: boolean }>`
  padding-bottom: 3rem;
  border-bottom: 1px solid ${({ $last }) => ($last ? 'transparent' : '#d8d8d8')};
  height: fit-content;
  cursor: pointer;

  @media (min-width: 48em) {
    max-width: 35rem;
  }
`;

const ItemTitle = styled.p<{ $active: boolean }>`
  margin: 0;
  color: ${({ $active }) => ($active ? '#1a1b1f' : '#bcc4d6')};
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 1.8rem;
  transition: color 0.6s ease-in-out;

  @media (min-width: 48em) {
    font-size: 2.4rem;
  }
`;

const ItemDesc = styled.p`
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.5rem;
  line-height: 1.5;

  @media (min-width: 48em) {
    font-size: 2rem;
    margin-top: 1rem;
  }
`;

const InlineImage = styled.img`
  display: block;
  width: 100%;
  margin-top: 2rem;
  margin-left: auto;
  object-fit: contain;

  @media (min-width: 48em) {
    display: none;
  }
`;

const PanelImageWrap = styled.div`
  display: none;

  @media (min-width: 48em) {
    display: block;
    margin-left: auto;
    width: 54.8rem;
    max-width: 100%;
    height: 54.8rem;
  }
`;

const PanelImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

function AboutApproach({ eyebrow, heading, description, cards }: AboutApproachProps) {
  const [tab, setTab] = useState(0);
  const active = cards[tab] ?? cards[0]!;

  return (
    <Section>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <HeadRow>
        <Heading>{heading}</Heading>
        {description ? <HeadDesc>{description}</HeadDesc> : null}
      </HeadRow>

      <Body>
        <List>
          {cards.map((card, i) => (
            <Item key={`${card.title}-${i}`}>
              <Rail $active={i === tab}>{i === tab ? <RailFill /> : null}</Rail>
              <ItemBody $last={i === cards.length - 1} onClick={() => setTab(i)}>
                <ItemTitle $active={i === tab}>{card.title}</ItemTitle>
                {i === tab && card.description ? <ItemDesc>{card.description}</ItemDesc> : null}
                {i === tab && card.imageUrl ? <InlineImage src={card.imageUrl} alt="" /> : null}
              </ItemBody>
            </Item>
          ))}
        </List>
        {active.imageUrl ? (
          <PanelImageWrap>
            <PanelImage src={active.imageUrl} alt="" />
          </PanelImageWrap>
        ) : null}
      </Body>
    </Section>
  );
}

export const aboutApproachBlock: BlockDefinition<AboutApproachProps> = {
  type: 'aboutApproach',
  schema: aboutApproachSchema,
  Renderer: AboutApproach,
};
